/**
 * Icon + semantic tint per dashboard KPI, keyed by the DTO field name.
 *
 * The mockups tint each KPI's icon tile by what the number MEANS, not by which
 * section it sits in — so a row of tiles reads at a glance: red needs a human,
 * amber is pending, green is settled, blue/navy is informational volume.
 *
 * Most role-dashboard KPI keys are tow-request statuses in disguise
 * (`open`, `winner`, `toArrive`, `inProcess`, `delivered`), so their tones are
 * derived from the same `statusTone()` map every list uses — a KPI and the
 * status pills it drills into stay the same colour.
 *
 * Icons are stored as COMPONENTS rather than rendered elements so this module
 * needs no JSX and can stay a `.ts` file — a `.tsx` exporting only helpers
 * trips `react-refresh/only-export-components`. Callers render them:
 *
 *   const { Icon, tone } = kpiVisual(key);
 *   <KpiCard icon={<Icon />} tone={tone} … />
 */
import type { SvgIconComponent } from '@mui/icons-material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BuildIcon from '@mui/icons-material/Build';
import BusinessIcon from '@mui/icons-material/Business';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ReplayIcon from '@mui/icons-material/Replay';
import { statusTone, type StatusTone } from '@/core/utils/statusTone';

export interface KpiVisual {
  Icon: SvgIconComponent;
  tone: StatusTone;
}

/**
 * KPI key → the tow-request status it represents, where one exists. Lets the
 * tone come from `statusTone()` rather than being restated here.
 *
 * `delivered` maps to COMPLETED on the wire (confirmed in the Angular
 * home.component.ts that the React app replaced).
 */
const KEY_TO_STATUS: Record<string, string> = {
  open: 'OPEN',
  openWithoutOffer: 'OPEN',
  winner: 'WINNER',
  toArrive: 'TOARRIVE',
  inProcess: 'INPROCESS',
  delivered: 'COMPLETED',
  completed: 'COMPLETED',
  cancelled: 'CANCEL',
  closed: 'CLOSE',
};

const KEY_TO_ICON: Record<string, SvgIconComponent> = {
  open: FolderOpenIcon,
  // A request nobody has bid on is the one that needs a human.
  openWithoutOffer: ErrorOutlineIcon,
  winner: EmojiEventsIcon,
  toArrive: HourglassEmptyIcon,
  inProcess: LocalShippingIcon,
  delivered: CheckCircleOutlineIcon,
  completed: CheckCircleOutlineIcon,
  closed: CheckCircleOutlineIcon,
  cancelled: ErrorOutlineIcon,
  // Non-status KPIs.
  unreadMessage: ChatBubbleOutlineIcon,
  newClaims: DescriptionOutlinedIcon,
  closedClaims: CheckCircleOutlineIcon,
  reopenClaims: ReplayIcon,
  openClaims: FolderOpenIcon,
  claims: DescriptionOutlinedIcon,
  roadSideAssintence: BuildIcon,
  towRequest: LocalShippingIcon,
  towRequestWhitoutTowProvider: ErrorOutlineIcon,
  // Admin dashboard "new records this week" counters.
  newAdjusters: ManageAccountsIcon,
  newCustomers: BusinessIcon,
  newDrivers: DriveEtaIcon,
};

/** Tones for KPIs that aren't a status in disguise. */
const KEY_TO_TONE: Record<string, StatusTone> = {
  unreadMessage: 'info',
  newClaims: 'primary',
  closedClaims: 'success',
  reopenClaims: 'warning',
  openClaims: 'info',
  claims: 'primary',
  roadSideAssintence: 'warning',
  towRequest: 'info',
  towRequestWhitoutTowProvider: 'error',
  newAdjusters: 'primary',
  newCustomers: 'primary',
  newDrivers: 'primary',
};

/**
 * Returns the icon component and tone for a KPI key. Unknown keys fall back to
 * a neutral tile rather than throwing, so a new backend field still renders
 * sanely before anyone gets round to giving it an icon.
 */
export function kpiVisual(key: string): KpiVisual {
  const status = KEY_TO_STATUS[key];
  return {
    Icon: KEY_TO_ICON[key] ?? AssignmentIcon,
    tone: status !== undefined ? statusTone(status) : (KEY_TO_TONE[key] ?? 'primary'),
  };
}

/**
 * `.eyebrow` from the mockups — a small tracked uppercase label that groups the
 * KPI rows without competing with the page title for weight.
 */
export const sectionEyebrowSx = {
  fontSize: '0.6875rem',
  fontWeight: 700,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: 'text.secondary',
  mb: 1.25,
} as const;
